import React, { useState, useEffect } from 'react'
import _ from 'lodash'
import Actions from '../../../../actions'
import { Spinner } from '../../../../components/generic'
import { history } from '../../../../store'
import { useDispatch } from 'react-redux'
import useResidents from './hooks/useResidents'
import useCreateResident from 'hooks/useCreateResident'
import {
  Home,
  ResidentItem,
  ResidentStatus,
  ResidentCreateBody
} from '@lmc/ts-common'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { matchesSearch } from './utils'
import { useTranslation } from 'react-i18next'
import {
  ResidentListHeader,
  ResidentListItem,
  ResidentSideFilters
} from './components'
import { getNominative, showToast } from '../../../../common/utils'
import { isBrowser, isTablet } from 'react-device-detect'
import { EmptyState, NoDataIcon } from '@lmc/ui-kit'
import Operations from '../../../../operations'
import { Link } from 'react-router-dom'
import useZones from 'hooks/useZones'
import useHomeType from 'hooks/useHomeType'
import {
  AddResidentModal2,
  PartialResident
} from '../../../../components/modals/AddResidentModal2/AddResidentModal2'

const StyledRowWrapper = styled.div`
  display: flex;
  margin-top: 0px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    max-height: max-content;
    overflow-y: auto;
    overflow-x: hidden;
  }
`

const StyledMainContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  max-width: 2000px;
  height: calc(100vh - 109px);
  margin-left: auto;
  margin-right: auto;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    height: calc(100vh - 63px);
  }
`

const StyledUserInfoContainer = styled.div`
  padding-top: 54px;
  margin: 0 24px;
  height: 100%;
`

const StyledScrollerDesktop = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 160px);
  overflow-y: auto;
  width: 100%;
`

type ResidentListProps = {
  testId?: string
}

const ResidentList: React.FC<ResidentListProps> = ({ testId }) => {
  const dispatch = useDispatch()

  const [search, setSearch] = useState<string>('')

  const [zones, setZones] = useState<string[]>([])

  const [remarks, setRemarks] = useState<string[]>([])

  const [statuses, setStatuses] = useState<string[]>(['active', 'dummy'])

  // New modal state
  const [isNewModalOpen, setIsNewModalOpen] = useState(false)

  const { residents, isLoading, fetchCarers } = useResidents()

  const { isPro } = useHomeType()

  const features = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const countableResidents =
    residents?.filter(
      (resident) =>
        resident.status &&
        [
          ResidentStatus.active,
          ResidentStatus.inactive,
          ResidentStatus.hospitalised
        ].includes(resident.status)
    ) || []

  const residentCount = countableResidents.length

  const { zones: zonesData, fetchZones } = useZones()

  const { t } = useTranslation()

  useEffect(() => {
    if (features?.pro) {
      fetchZones()
    }
    const intervalId = setInterval(fetchCarers, 600000)

    fetchCarers()
    return () => {
      clearInterval(intervalId)
    }
  }, [features?.pro, fetchZones, fetchCarers])

  // New modal handlers
  const openNewModal = () => {
    setIsNewModalOpen(true)
  }

  const closeNewModal = () => {
    setIsNewModalOpen(false)
  }

  const handleResidentCreated = (resident: ResidentItem) => {
    // Navigate to the newly created resident's profile
    history.push(`/residents/${resident.id}/profile`)
  }

  const { createResident } = useCreateResident()

  const handleCreateResident = async (residentData: ResidentCreateBody) => {
    const resident = await createResident(residentData)
    // Update Redux state with new residents list
    dispatch(Operations.loadList('residents'))
    return resident
  }

  const hasResidents = !!residents?.length

  const searchResults = _.filter(residents, (resident) =>
    matchesSearch(resident, statuses, zones, remarks, search)
  )

  if (isLoading)
    return (
      <Spinner
        data-testid={testId ? `${testId}-loading` : 'residents-list-loading'}
      />
    )

  const hasSearchResults = !!searchResults?.length

  const residentList = hasSearchResults ? (
    searchResults.map((resident: ResidentItem, index: number) => (
      <ResidentListItem
        firstItem={index === 0}
        key={resident.id}
        resident={resident}
        testId={testId}
        index={index}
        onClick={(e: { stopPropagation: () => void }) => {
          e.stopPropagation()
          history.push(`/residents/${resident.id}/profile`)
        }}
      />
    ))
  ) : (
    <EmptyState
      title={t(`general.emptyState.noResidentsListResultTitle`, {
        variable: getNominative(
          preferredPeopleNominative,
          'Resident'
        )?.toLowerCase()
      })}
      description={t(`general.emptyState.noTeamListResultDescription`)}
      icon={<NoDataIcon />}
      data-testid={
        testId ? `${testId}-no-results` : 'residents-list-no-results'
      }
    />
  )

  const handleSubscribePro = () => {
    dispatch(Operations.openProPlanCheckout({}))
  }

  return (
    <StyledMainContainer
      data-testid={testId ? `${testId}-residents-list` : 'residents-list'}
    >
      <ResidentListHeader
        openModal={openNewModal}
        onSearch={setSearch}
        title={getNominative(preferredPeopleNominative, 'Residents')}
        searchPlaceholder={t(`general.team.typeToSearch`)}
        buttonTitle={t(`general.resident.addServiceUser`, {
          variable: getNominative(preferredPeopleNominative, 'Resident')
        })}
        isPro={isPro}
        residentCount={residentCount}
        preferredPeopleNominative={preferredPeopleNominative}
        testId={testId}
      />
      {hasResidents ? (
        <StyledRowWrapper
          data-testid={
            testId ? `${testId}-residents-content` : 'residents-content'
          }
        >
          <ResidentSideFilters
            zonesData={zonesData}
            onZones={setZones}
            onStatuses={setStatuses}
            onRemarks={setRemarks}
            statuses={statuses}
            zones={zones}
            remarks={remarks}
            features={features}
            clearButtonTitle={t(`general.buttons.clear`)}
            statusTitle={t(`general.team.status`)}
            remarksTitle={t(`general.resident.remarks`)}
            zoneTitle={t(`general.team.zone`)}
            residents={residents}
            noZoneTitle={t(`general.team.noZone`)}
            viewProTitle={t(`general.team.viewPro`)}
            onSubscribe={handleSubscribePro}
            onDefaultOpened={isDesktop}
            testId={testId}
          />

          {isDesktop ? (
            <StyledScrollerDesktop
              data-cy="residents-list"
              data-testid={
                testId ? `${testId}-residents-scroller` : 'residents-scroller'
              }
            >
              {residentList}
            </StyledScrollerDesktop>
          ) : (
            <div
              data-testid={
                testId
                  ? `${testId}-residents-mobile-list`
                  : 'residents-mobile-list'
              }
            >
              {residentList}
            </div>
          )}
        </StyledRowWrapper>
      ) : (
        <StyledUserInfoContainer
          data-testid={testId ? `${testId}-no-residents` : 'no-residents'}
        >
          <EmptyState
            title={t(`general.emptyState.noServiceUsersResponseTitle`, {
              variable: getNominative(
                preferredPeopleNominative,
                'Residents'
              )?.toLowerCase()
            })}
            description={t(
              `general.emptyState.noServiceUsersResponseDescription`,
              {
                variable: getNominative(
                  preferredPeopleNominative,
                  'Residents'
                )?.toLowerCase()
              }
            )}
            icon={<NoDataIcon />}
            data-testid={
              testId ? `${testId}-empty-state` : 'residents-empty-state'
            }
          />
        </StyledUserInfoContainer>
      )}

      {/* New AddResidentModal2 */}
      <AddResidentModal2
        isModalVisible={isNewModalOpen}
        handleToggleModal={closeNewModal}
        residents={(residents as PartialResident[]) || []}
        preferredPeopleNominative={preferredPeopleNominative}
        carePlansEnabled={!!features?.carePlans}
        createResident={handleCreateResident}
        onSuccess={handleResidentCreated}
        testId={testId}
      />
    </StyledMainContainer>
  )
}

const isDesktop = isBrowser || isTablet

export default ResidentList
